import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Users, Clock, UserX, Plus, Trash2, ShieldCheck, CheckCircle, UserCheck, Video,
} from 'lucide-react';
import { format } from 'date-fns';

const getStatusIcon = (status) => {
  switch (status) {
    case 'present_onsite': return <UserCheck className="h-4 w-4 text-green-600" />;
    case 'present_virtual': return <Video className="h-4 w-4 text-blue-600" />;
    case 'late': return <Clock className="h-4 w-4 text-amber-600" />;
    default: return <UserX className="h-4 w-4 text-gray-400" />;
  }
};

const ConsoleAttendancePanel = ({
  users, attendance, getAttendanceStatus, updateAttendance,
  lateArrivals, addLateArrival, removeLateArrival, lateSelectUser, setLateSelectUser,
  leftEarly, addLeftEarly, removeLeftEarly, leftEarlySelectUser, setLeftEarlySelectUser,
  proxies, revokeProxy, toggleProxyAuthority, shareholders, getUserName, onAddProxy,
}) => {
  return (
    <Card className="border-[#E2E6EA] lg:col-span-1">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4 text-[#00A3A3]" />Attendance & Proxies</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="px-4 pb-2"><p className="text-xs font-medium text-[#5A6570]">Participants</p></div>
        <div className="divide-y divide-[#E2E6EA] max-h-[320px] overflow-y-auto">
          {users.map((u) => {
            const status = getAttendanceStatus(u.id);
            return (
              <div key={u.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-[#F8F9FA] transition-colors" data-testid={`attendance-${u.id}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                  {getStatusIcon(status)}
                  <div className="min-w-0">
                    <p className="font-medium text-sm text-[#1A1D21] truncate">{u.full_name}</p>
                    <p className="text-[10px] text-[#5A6570] capitalize">{u.role?.replace('_', ' ')}</p>
                  </div>
                </div>
                <Select value={status} onValueChange={(v) => updateAttendance(u.id, v)}>
                  <SelectTrigger className="w-[120px] h-7 text-[10px] border-[#E2E6EA]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="present_onsite">Present (Onsite)</SelectItem>
                    <SelectItem value="present_virtual">Present (Virtual)</SelectItem>
                    <SelectItem value="absent">Absent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            );
          })}
        </div>

        {/* Late Arrivals */}
        <div className="border-t border-[#E2E6EA] px-4 pt-3 pb-2">
          <p className="text-xs font-medium text-[#5A6570] flex items-center gap-1.5 mb-2"><Clock className="h-3.5 w-3.5 text-amber-500" />Late Arrivals</p>
          {lateArrivals.length > 0 && (
            <div className="space-y-1 mb-2">
              {lateArrivals.map(l => (
                <div key={l.user_id} className="flex items-center justify-between text-xs p-1.5 rounded bg-amber-50 border border-amber-100" data-testid={`late-${l.user_id}`}>
                  <div className="min-w-0 flex-1">
                    <span className="font-medium text-[#1A1D21]">{l.user_name}</span>
                    <span className="text-amber-600 ml-2">{format(new Date(l.time), 'h:mm a')}</span>
                  </div>
                  <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-red-400" onClick={() => removeLateArrival(l.user_id)}><Trash2 className="h-3 w-3" /></Button>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-1.5">
            <Select value={lateSelectUser} onValueChange={setLateSelectUser}>
              <SelectTrigger className="flex-1 h-7 text-[10px] border-[#E2E6EA]" data-testid="late-select"><SelectValue placeholder="Select participant..." /></SelectTrigger>
              <SelectContent>
                {users.filter(u => {
                  const s = getAttendanceStatus(u.id);
                  return (s === 'present_onsite' || s === 'present_virtual') && !lateArrivals.some(l => l.user_id === u.id);
                }).map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" className="h-7 text-[10px] bg-amber-500 hover:bg-amber-600 px-2" onClick={() => addLateArrival(lateSelectUser)} disabled={!lateSelectUser} data-testid="add-late-btn"><Plus className="h-3 w-3" /></Button>
          </div>
        </div>

        {/* Left Early */}
        <div className="border-t border-[#E2E6EA] px-4 pt-3 pb-2">
          <p className="text-xs font-medium text-[#5A6570] flex items-center gap-1.5 mb-2"><UserX className="h-3.5 w-3.5 text-red-400" />Left Early</p>
          {leftEarly.length > 0 && (
            <div className="space-y-1 mb-2">
              {leftEarly.map(l => (
                <div key={l.user_id} className="flex items-center justify-between text-xs p-1.5 rounded bg-red-50 border border-red-100" data-testid={`left-early-${l.user_id}`}>
                  <div className="min-w-0 flex-1">
                    <span className="font-medium text-[#1A1D21]">{l.user_name}</span>
                    <span className="text-red-500 ml-2">{format(new Date(l.time), 'h:mm a')}</span>
                  </div>
                  <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-red-400" onClick={() => removeLeftEarly(l.user_id)}><Trash2 className="h-3 w-3" /></Button>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-1.5">
            <Select value={leftEarlySelectUser} onValueChange={setLeftEarlySelectUser}>
              <SelectTrigger className="flex-1 h-7 text-[10px] border-[#E2E6EA]" data-testid="left-early-select"><SelectValue placeholder="Select participant..." /></SelectTrigger>
              <SelectContent>
                {users.filter(u => {
                  const s = getAttendanceStatus(u.id);
                  return (s === 'present_onsite' || s === 'present_virtual') && !leftEarly.some(l => l.user_id === u.id);
                }).map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" className="h-7 text-[10px] bg-red-500 hover:bg-red-600 px-2" onClick={() => addLeftEarly(leftEarlySelectUser)} disabled={!leftEarlySelectUser} data-testid="add-left-early-btn"><Plus className="h-3 w-3" /></Button>
          </div>
        </div>

        {/* Proxies */}
        <div className="border-t border-[#E2E6EA] px-4 pt-3 pb-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-[#5A6570] flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" />Proxies & Representatives</p>
            <Button size="sm" variant="ghost" className="h-6 text-[10px] text-[#00A3A3]" onClick={onAddProxy} data-testid="add-proxy-btn"><Plus className="h-3 w-3 mr-0.5" />Add</Button>
          </div>
          {proxies.length === 0 ? (
            <p className="text-[10px] text-[#A8B1B9] text-center py-2">No proxies recorded</p>
          ) : (
            <div className="space-y-1.5 max-h-[180px] overflow-y-auto">
              {proxies.map(p => (
                <div key={p.id} className={`p-2 rounded border text-xs ${p.is_valid !== false ? 'border-[#E2E6EA]' : 'border-red-200 bg-red-50/50'}`} data-testid={`proxy-${p.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-[#1A1D21] truncate">
                        {shareholders.find(s => s.id === p.principal_id)?.name || getUserName(p.principal_id)} &rarr; {shareholders.find(s => s.id === p.proxy_holder_id)?.name || getUserName(p.proxy_holder_id)}
                      </p>
                      {p.shares_covered > 0 && <p className="text-[10px] text-[#5A6570]">{(p.shares_covered || 0).toLocaleString()} shares</p>}
                      {p.authority_doc && <p className="text-[10px] text-[#5A6570] italic">{p.authority_doc}</p>}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {p.is_valid !== false && (
                        <label className="flex items-center gap-1 cursor-pointer" title="Secretary confirms authority">
                          <Checkbox checked={p.authority_confirmed || false} onCheckedChange={() => toggleProxyAuthority(p.id)} className="h-3.5 w-3.5" />
                          <span className="text-[9px] text-[#5A6570]">Confirm</span>
                        </label>
                      )}
                      {p.is_valid !== false ? (
                        <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-red-400" onClick={() => revokeProxy(p.id)}><Trash2 className="h-3 w-3" /></Button>
                      ) : (
                        <Badge variant="outline" className="text-[9px] text-red-500 border-red-200 py-0">Revoked</Badge>
                      )}
                    </div>
                  </div>
                  {p.authority_confirmed && <p className="text-[9px] text-green-600 mt-0.5 flex items-center gap-0.5"><CheckCircle className="h-2.5 w-2.5" />Authority confirmed by Secretary</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ConsoleAttendancePanel;
